import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CVHttpService } from '../services';
import { HttpService } from 'core/services/http';
import {
    Box,
    CircularProgress,
    Typography,
    Stack,
    Divider,
    Container,
    Paper
} from '@mui/material';
import { Utils } from 'core/utils';
import MarkdownRenderer from '../components/MarkdownRenderer';
import CompactMarkdownRenderer from '../components/CompactMarkdownRenderer';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
        return dateString;
    }
};

function PrintableCVPage() {
    const { shareUUID, id } = useParams();
    const [cv, setCv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sections, setSections] = useState({
        experiences: [],
        education: [],
        skills: [],
        languages: [],
        certificates: [],
        references: [],
        awards: []
    });

    useEffect(() => {
        loadCV();
    }, [shareUUID, id]);

    useEffect(() => {
        if (cv?.id) {
            loadSections();
        }
    }, [cv]);

    useEffect(() => {
        // Trigger print dialog when page loads
        if (!loading && cv) {
            window.onload = () => {
                setTimeout(() => {
                    window.print();
                }, 500);
            };
        }
    }, [loading, cv]);

    const loadCV = async () => {
        try {
            let data;
            if (shareUUID) {
                data = await CVHttpService.loadCVByShareUUID(shareUUID);
            } else if (id) {
                data = await CVHttpService.loadCV(id);
            } else {
                setLoading(false);
                return;
            }
            setCv(data);
        } catch (err) {
            console.error('Error loading CV:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadSections = async () => {
        if (!cv?.id) return;
        try {
            const service = new HttpService();
            // Send share_uuid parameter if available for security verification
            const shareUuidParam = shareUUID ? `&share_uuid=${shareUUID}` : '';
            const [experiences, education, skills, languages, certificates, references, awards] = await Promise.all([
                service.get(`/job/cv/jobexperience/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/education/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/userskill/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/userlanguage/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/certificate/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/reference/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/award/?cv=${cv.id}${shareUuidParam}`, null).catch(() => ({ results: [] }))
            ]);

            setSections({
                experiences: Array.isArray(experiences) ? experiences : (experiences?.results || []),
                education: Array.isArray(education) ? education : (education?.results || []),
                skills: Array.isArray(skills) ? skills : (skills?.results || []),
                languages: Array.isArray(languages) ? languages : (languages?.results || []),
                certificates: Array.isArray(certificates) ? certificates : (certificates?.results || []),
                references: Array.isArray(references) ? references : (references?.results || []),
                awards: Array.isArray(awards) ? awards : (awards?.results || [])
            });
        } catch (err) {
            console.error('Error loading sections:', err);
        }
    };

    if (loading || !cv) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    const locationParts = [cv.city, cv.country?.title || cv.country].filter(Boolean);
    const location = locationParts.length > 0 ? locationParts.join(', ') : '';

    return (
        <Box sx={{ '@media print': { padding: 0, margin: 0, background: 'white !important', backgroundImage: 'none !important' } }}>
            <style>
                {`
                    @media print {
                        @page {
                            margin: 1cm;
                            size: A4;
                        }
                        body {
                            margin: 0;
                            padding: 0;
                            background: none !important;
                            background-image: none !important;
                            background-color: white !important;
                        }
                        html {
                            background: none !important;
                            background-image: none !important;
                            background-color: white !important;
                        }
                        .main {
                            background: none !important;
                            background-image: none !important;
                            background-color: white !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .print-break {
                            page-break-after: always;
                        }
                        .print-avoid-break {
                            page-break-inside: avoid;
                        }
                        * {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                `}
            </style>
            <Container maxWidth="md" sx={{ py: 4, '@media print': { py: 0, maxWidth: '100%', margin: 0, padding: '20px', background: 'white', backgroundImage: 'none' } }}>
                <Paper elevation={0} sx={{ p: 4, '@media print': { p: 0, boxShadow: 'none', pageBreakInside: 'avoid', background: 'white', backgroundImage: 'none' } }}>
                    {/* Header - Only show for non-AI CVs */}
                    {(() => {
                        const isAIGenerated = cv.description && (
                            cv.description.trim().startsWith('#') || 
                            cv.description.includes('##') ||
                            (cv.description.includes('---') && cv.description.split('\n').length > 10)
                        );
                        
                        if (isAIGenerated) {
                            // For AI-generated CVs, header is handled in CompactMarkdownRenderer
                            return null;
                        }
                        
                        return (
                            <>
                                <Box sx={{ mb: 4, textAlign: 'center', '@media print': { mb: 1, mt: 0, pageBreakAfter: 'avoid' } }} className="print-avoid-break">
                                    <Typography variant="h3" fontWeight={700} gutterBottom sx={{ '@media print': { fontSize: '24px', mb: 0.5, pageBreakAfter: 'avoid' } }}>
                                        {formatText(cv.title)}
                                    </Typography>
                                    <Typography variant="h5" color="text.secondary" gutterBottom sx={{ '@media print': { fontSize: '16px', mb: 0.5, pageBreakAfter: 'avoid' } }}>
                                        {formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || '')}
                                    </Typography>
                                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ mt: 2, '@media print': { mt: 0.5, mb: 0 } }}>
                                        {cv.email && (
                                            <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>{formatText(cv.email)}</Typography>
                                        )}
                                        {cv.phone && (
                                            <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>{formatText(cv.phone)}</Typography>
                                        )}
                                        {cv.website && (
                                            <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>{formatText(cv.website)}</Typography>
                                        )}
                                        {location && (
                                            <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>{location}</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Divider sx={{ mb: 4, '@media print': { mb: 2, display: 'none' } }} />
                            </>
                        );
                    })()}

                    {/* Check if this is an AI-generated CV */}
                    {(() => {
                        const isAIGenerated = cv.description && (
                            cv.description.trim().startsWith('#') || 
                            cv.description.includes('##') ||
                            (cv.description.includes('---') && cv.description.split('\n').length > 10)
                        );
                        
                        if (isAIGenerated) {
                            // For AI-generated CVs, show the full markdown content with compact format
                            return (
                                <Box 
                                    sx={{ 
                                        mb: 4, 
                                        '@media print': { 
                                            mb: 0,
                                            mt: 0.5,
                                            pageBreakBefore: 'auto',
                                            pageBreakAfter: 'auto'
                                        } 
                                    }}
                                >
                                    <CompactMarkdownRenderer markdown={cv.description} />
                                </Box>
                            );
                        }
                        
                        // For regular CVs, show structured format
                        return (
                            <>
                                {/* Description */}
                                {cv.description && (
                                    <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                            About
                                        </Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', '@media print': { fontSize: '12px' } }}>
                                            {formatText(cv.description)}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Work Experience */}
                                {sections.experiences.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Work Experience
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={3}>
                                {sections.experiences.map((exp) => (
                                    <Box key={exp.id}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ '@media print': { fontSize: '14px' } }}>
                                                    {formatText(exp.title)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ '@media print': { fontSize: '11px' } }}>
                                                    {formatText(exp.company_title)}
                                                </Typography>
                                            </Box>
                                            {(exp.start_date || exp.end_date) && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap', '@media print': { fontSize: '11px' } }}>
                                                    {formatDate(exp.start_date)} - {exp.end_date ? formatDate(exp.end_date) : 'Present'}
                                                </Typography>
                                            )}
                                        </Box>
                                        {exp.description && (
                                            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', '@media print': { fontSize: '11px' } }}>
                                                {formatText(exp.description)}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                                )}

                                {/* Education */}
                                {sections.education.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Education
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={2}>
                                {sections.education.map((edu) => (
                                    <Box key={edu.id}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ '@media print': { fontSize: '14px' } }}>
                                                    {formatText(edu.level)}
                                                </Typography>
                                                {edu.field && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ '@media print': { fontSize: '11px' } }}>
                                                        {formatText(edu.field)}
                                                    </Typography>
                                                )}
                                                {edu.location && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ '@media print': { fontSize: '11px' } }}>
                                                        {formatText(edu.location)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {(edu.start_date || edu.end_date) && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap', '@media print': { fontSize: '11px' } }}>
                                                    {formatDate(edu.start_date)} - {edu.end_date ? formatDate(edu.end_date) : 'Present'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                                )}

                                {/* Skills */}
                                {sections.skills.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Skills
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {sections.skills.map((skill) => (
                                    <Typography 
                                        key={skill.id} 
                                        variant="body2" 
                                        sx={{ 
                                            mr: 2, 
                                            mb: 1,
                                            '@media print': { fontSize: '11px' }
                                        }}
                                    >
                                        • {formatText(skill.title)}
                                        {skill.years_of_experience && ` (${skill.years_of_experience})`}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                                )}

                                {/* Languages */}
                                {sections.languages.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Languages
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={1}>
                                {sections.languages.map((lang) => (
                                    <Typography key={lang.id} variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>
                                        <strong>{formatText(lang.title)}</strong>
                                        {lang.proficiency && ` - ${lang.proficiency}`}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>
                                )}

                                {/* Certificates */}
                                {sections.certificates.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Certificates & Licenses
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={2}>
                                {sections.certificates.map((cert) => (
                                    <Box key={cert.id}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ '@media print': { fontSize: '14px' } }}>
                                                    {formatText(cert.title)}
                                                </Typography>
                                                {cert.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ '@media print': { fontSize: '11px' } }}>
                                                        {formatText(cert.description)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {(cert.expiration_month || cert.expiration_year) && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap', '@media print': { fontSize: '11px' } }}>
                                                    Expires: {cert.expiration_month}/{cert.expiration_year}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                                )}

                                {/* Awards */}
                                {sections.awards.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Awards & Achievements
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={2}>
                                {sections.awards.map((award) => (
                                    <Box key={award.id}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ '@media print': { fontSize: '14px' } }}>
                                                    {formatText(award.title)}
                                                </Typography>
                                                {award.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, '@media print': { fontSize: '11px' } }}>
                                                        {formatText(award.description)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {award.awarded_date && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap', '@media print': { fontSize: '11px' } }}>
                                                    {formatDate(award.awarded_date)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                                )}

                                {/* References */}
                                {sections.references.length > 0 && (
                        <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                Professional References
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={2}>
                                {sections.references.map((ref) => (
                                    <Box key={ref.id}>
                                        <Typography variant="subtitle1" fontWeight={600} sx={{ '@media print': { fontSize: '14px' } }}>
                                            {formatText(ref.name)}
                                        </Typography>
                                        {ref.position && (
                                            <Typography variant="body2" color="text.secondary" sx={{ '@media print': { fontSize: '11px' } }}>
                                                {formatText(ref.position)}
                                            </Typography>
                                        )}
                                        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                            {ref.email && (
                                                <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>
                                                    {formatText(ref.email)}
                                                </Typography>
                                            )}
                                            {ref.phone_number && (
                                                <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>
                                                    {formatText(ref.phone_number)}
                                                </Typography>
                                            )}
                                            {ref.location && (
                                                <Typography variant="body2" sx={{ '@media print': { fontSize: '11px' } }}>
                                                    {formatText(ref.location)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                                )}

                                {/* Interests */}
                                {cv.interests && (
                                    <Box sx={{ mb: 4, '@media print': { mb: 3 } }} className="print-avoid-break">
                                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ '@media print': { fontSize: '16px' } }}>
                                            Interests
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', '@media print': { fontSize: '11px' } }}>
                                            {formatText(cv.interests)}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        );
                    })()}
                </Paper>
            </Container>
        </Box>
    );
}

export default PrintableCVPage;

